import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DebugProvider } from './contexts/DebugContext';
import { SiteConfigProvider } from './contexts/SiteConfigContext';
import { useTheme } from './contexts/ThemeContext';
import { setNavigateFunction } from './services/api';
import ErrorBoundary from './components/ErrorBoundary';
import errorLogger from './utils/errorLogger';
import simpleErrorHandler from './utils/simpleErrorHandler';
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';
import GlobalDebugPanel from './components/Debug/GlobalDebugPanel';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import TestError from './pages/TestError';
import SocketDiagnostic from './pages/SocketDiagnostic';
import SocketTest from './pages/SocketTest';
import Dashboard from './pages/Dashboard';
import Servers from './pages/Servers';
import ServerDetail from './pages/Servers/ServerDetail';
import CreateServer from './pages/Servers/CreateServer';
import CreateServerWithActivationCode from './pages/Servers/CreateServerWithActivationCode';
import Profile from './pages/Profile';
import APITest from './pages/APITest';
import Admin from './pages/Admin';
import PricingTable from './pages/Public/PricingTable';
import GameIntro from './pages/Public/GameIntro';
import HomePage from './pages/Home/HomePage';
import ServerPricingManagement from './pages/Admin/ServerPricingManagement';
import CouponManagement from './pages/Admin/CouponManagement';
import ModernThemeManagement from './pages/Admin/ModernThemeManagement';
import ActivationCodes from './pages/Admin/ActivationCodes';
import VersionManagement from './pages/Admin/VersionManagement';
import TestProgressPage from './pages/TestProgressPage';
import ServerShare from './pages/Share/ServerShare';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentNotify from './pages/PaymentNotify';
import LoadingSpinner from './components/Common/LoadingSpinner';
import PanelTodo from './pages/PanelTodo';
import './styles/global.css';

const { Content } = Layout;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const DefaultRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  // 如果用户已登录，重定向到仪表板；否则重定向到主页
  if (user) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/" replace />;
  }
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { antdTheme } = useTheme();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  console.log('🏗️ AppLayout渲染 - user:', !!user, 'loading:', loading, 'pathname:', location.pathname);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 主页、价格表、游戏介绍和分享页面不需要布局
  if (location.pathname === '/' || location.pathname === '/pricing' || location.pathname.startsWith('/game/') || location.pathname.startsWith('/share/')) {
    return <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>;
  }
  
  // 登录、注册和找回密码页面不需要布局，但如果用户已登录则重定向到仪表板
  if (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/forgot-password') {
    if (user) {
      return <Navigate to="/dashboard" replace />;
    }
    return <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>;
  }
  
  // 如果认证状态还在加载中，显示加载状态
  if (loading) {
    return <ConfigProvider theme={antdTheme}><LoadingSpinner /></ConfigProvider>;
  }
  
  // 只有在认证状态加载完成后才检查用户状态
  if (!user) {
    console.log('🚨 AppLayout: 用户未登录，跳转到登录页面');
    return <Navigate to="/login" replace />;
  }

  return (
    <ConfigProvider theme={antdTheme}>
      <Layout style={{ minHeight: '100vh' }}>
        <Header />
        <Layout>
          <Sidebar />
          <Layout 
            style={{ 
              marginLeft: isMobile ? '0' : '256px',
              background: 'var(--background-color)',
              minHeight: 'calc(100vh - 64px)',
              width: isMobile ? '100%' : 'calc(100% - 256px)',
              maxWidth: isMobile ? '100%' : 'calc(100% - 256px)',
              position: isMobile ? 'relative' : 'static',
              left: isMobile ? '0' : 'auto',
              right: isMobile ? '0' : 'auto',
              boxSizing: 'border-box',
              transform: isMobile ? 'none' : 'auto'
            }}
            className="main-content-layout"
          >
            <Content 
              style={{ 
                padding: isMobile ? '16px' : '24px',
                background: 'var(--background-color)',
                minHeight: 'calc(100vh - 64px)'
              }}
              className="main-content"
            >
              {children}
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  const navigate = useNavigate();
  
  // 设置API服务的导航函数
  React.useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  // 全局错误处理
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('未处理的Promise拒绝:', event.reason);
      
      // 记录错误到错误日志系统
      errorLogger.logError(event.reason, 'UnhandledRejection', {
        type: 'PromiseRejection',
        reason: event.reason
      });
      
      // 同时记录到简单错误处理器
      simpleErrorHandler.logError(event.reason, 'UnhandledRejection');
      
      // 检查是否是网络错误
      if (event.reason?.code === 'ECONNRESET' || 
          event.reason?.code === 'ECONNABORTED' ||
          event.reason?.code === 'ENOTFOUND' ||
          event.reason?.code === 'ETIMEDOUT' ||
          event.reason?.code === 'ECONNREFUSED' ||
          event.reason?.code === 'EPIPE' ||
          event.reason?.message?.includes('Network Error') ||
          event.reason?.message?.includes('fetch') ||
          event.reason?.message?.includes('Failed to fetch') ||
          event.reason?.message?.includes('ERR_NETWORK') ||
          event.reason?.message?.includes('ERR_CONNECTION_REFUSED')) {
        console.log('检测到网络错误，应用将继续运行:', event.reason?.message);
        errorLogger.logWarning(`网络错误: ${event.reason?.message}`, 'NetworkError');
        event.preventDefault(); // 阻止默认的错误处理
        return;
      }
      
      // 检查是否是Socket.IO相关错误
      if (event.reason?.message?.includes('socket') || 
          event.reason?.message?.includes('WebSocket') ||
          event.reason?.message?.includes('Socket.IO') ||
          event.reason?.message?.includes('transport error')) {
        console.log('检测到Socket.IO错误，应用将继续运行:', event.reason?.message);
        errorLogger.logWarning(`Socket.IO错误: ${event.reason?.message}`, 'SocketIOError');
        event.preventDefault();
        return;
      }
      
      // 检查是否是超时错误
      if (event.reason?.message?.includes('timeout') ||
          event.reason?.message?.includes('TIMEOUT')) {
        console.log('检测到超时错误，应用将继续运行:', event.reason?.message);
        errorLogger.logWarning(`超时错误: ${event.reason?.message}`, 'TimeoutError');
        event.preventDefault();
        return;
      }
    };

    const handleError = (event: ErrorEvent) => {
      console.error('全局错误:', event.error);
      
      // 记录错误到错误日志系统
      errorLogger.logError(event.error, 'GlobalError', {
        type: 'ErrorEvent',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
      
      // 同时记录到简单错误处理器
      simpleErrorHandler.logError(event.error, 'GlobalError');
      
      // 检查是否是网络相关错误
      if (event.error?.code === 'ECONNRESET' || 
          event.error?.code === 'ECONNABORTED' ||
          event.error?.code === 'ENOTFOUND' ||
          event.error?.code === 'ETIMEDOUT' ||
          event.error?.code === 'ECONNREFUSED' ||
          event.error?.code === 'EPIPE' ||
          event.error?.message?.includes('Network Error') ||
          event.error?.message?.includes('fetch') ||
          event.error?.message?.includes('Failed to fetch') ||
          event.error?.message?.includes('ERR_NETWORK') ||
          event.error?.message?.includes('ERR_CONNECTION_REFUSED')) {
        console.log('检测到网络错误，应用将继续运行:', event.error?.message);
        errorLogger.logWarning(`网络错误: ${event.error?.message}`, 'NetworkError');
        event.preventDefault(); // 阻止默认的错误处理
        return;
      }
      
      // 检查是否是Socket.IO相关错误
      if (event.error?.message?.includes('socket') || 
          event.error?.message?.includes('WebSocket') ||
          event.error?.message?.includes('Socket.IO') ||
          event.error?.message?.includes('transport error')) {
        console.log('检测到Socket.IO错误，应用将继续运行:', event.error?.message);
        errorLogger.logWarning(`Socket.IO错误: ${event.error?.message}`, 'SocketIOError');
        event.preventDefault();
        return;
      }
      
      // 检查是否是超时错误
      if (event.error?.message?.includes('timeout') ||
          event.error?.message?.includes('TIMEOUT')) {
        console.log('检测到超时错误，应用将继续运行:', event.error?.message);
        errorLogger.logWarning(`超时错误: ${event.error?.message}`, 'TimeoutError');
        event.preventDefault();
        return;
      }
    };

    // 添加事件监听器
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // 清理函数
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <ErrorBoundary>
      <SiteConfigProvider>
        <DebugProvider>
          <AuthProvider>
            <AppLayout>
              <Routes>
                {/* 公开路由 */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/test-error" element={<TestError />} />
                <Route path="/socket-diagnostic" element={<SocketDiagnostic />} />
                <Route path="/socket-test" element={<SocketTest />} />
                <Route path="/share/server/:serverId" element={<ServerShare />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/notify" element={<PaymentNotify />} />
                
                {/* 受保护的路由 */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/servers" element={
                  <ProtectedRoute>
                    <Servers />
                  </ProtectedRoute>
                } />
                
                <Route path="/servers/create" element={
                  <ProtectedRoute>
                    <CreateServer />
                  </ProtectedRoute>
                } />
                
                <Route path="/servers/create-with-activation-code" element={
                  <ProtectedRoute>
                    <CreateServerWithActivationCode />
                  </ProtectedRoute>
                } />
                
                <Route path="/servers/create-old" element={
                  <ProtectedRoute>
                    <CreateServer />
                  </ProtectedRoute>
                } />
                
                <Route path="/servers/:serverId" element={
                  <ProtectedRoute>
                    <ServerDetail />
                  </ProtectedRoute>
                } />
                
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                
                <Route path="/panel-todo" element={
                  <ProtectedRoute>
                    <PanelTodo />
                  </ProtectedRoute>
                } />
                
                <Route path="/api-test" element={
                  <ProtectedRoute>
                    <APITest />
                  </ProtectedRoute>
                } />
                
                <Route path="/test-progress" element={
                  <ProtectedRoute>
                    <TestProgressPage />
                  </ProtectedRoute>
                } />
                
                {/* 管理员路由 */}
                <Route path="/admin/*" element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                } />
                
                <Route path="/admin/pricing" element={
                  <AdminRoute>
                    <ServerPricingManagement />
                  </AdminRoute>
                } />
                
                <Route path="/admin/coupons" element={
                  <AdminRoute>
                    <CouponManagement />
                  </AdminRoute>
                } />
                
                <Route path="/admin/themes" element={
                  <AdminRoute>
                    <ModernThemeManagement />
                  </AdminRoute>
                } />
                
                <Route path="/admin/activation-codes" element={
                  <AdminRoute>
                    <ActivationCodes />
                  </AdminRoute>
                } />
                
                <Route path="/admin/version-management" element={
                  <AdminRoute>
                    <VersionManagement />
                  </AdminRoute>
                } />
                
                         {/* 公开价格表页面 */}
                         <Route path="/pricing" element={<PricingTable />} />
                         
                         {/* 游戏介绍页面 */}
                         <Route path="/game/:gameId" element={<GameIntro />} />
                
                {/* 默认重定向 */}
                <Route path="*" element={<DefaultRoute />} />
              </Routes>
              
              {/* 全局调试面板 */}
              <GlobalDebugPanel />
              </AppLayout>
          </AuthProvider>
        </DebugProvider>
      </SiteConfigProvider>
    </ErrorBoundary>
  );
};

export default App;



